import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Box from '@mui/material/Box';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import FacebookIcon from '@mui/icons-material/Facebook';
import ShareIcon from '@mui/icons-material/Share';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Skeleton from '@mui/material/Skeleton';
import IconTooltip from '../IconTooltip';
import GroupsIcon from '@mui/icons-material/Groups';
import renderPercentage from '../renderPercentage';
import useApi from '../../hooks/useApi';

const getColor = source => {
    const colours = [
        { name: 'Twitter', colour: '#1DA1F2' },
        { name: 'Facebook', colour: '#4267B2' },
        { name: 'LinkedIn', colour: '#0077B5' },
        { name: 'Share', colour: '#444' },
        { name: 'Teams', colour: '#4b53bc' }
    ];

    return colours.find(e => e.name === source).colour;
};

const SocialTable = ({ source, totals, campaignUniqueOpens }) => {
    return (
        <TableContainer>
            <Table size="small">
                <TableBody>
                    {source !== 'Share' && source !== 'Teams' && (
                        <TableRow>
                            <TableCell component="th" width="100%">{source === 'Twitter' ? 'Tweets' : 'Shares'}</TableCell>
                            <TableCell align="right">{totals.shares}</TableCell>
                            <TableCell align="right">{renderPercentage(totals.shares, campaignUniqueOpens)}</TableCell>
                            <TableCell>
                                <IconTooltip title="Social Shares % - Number of Shares / Unique Email Opens" />
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell component="th" width="100%">Opens</TableCell>
                        <TableCell align="right">{totals.opens}</TableCell>
                        <TableCell align="right">{renderPercentage(totals.opens, totals.shares)}</TableCell>
                        <TableCell>
                            <IconTooltip title="Social Opens % - Number of Social Opens / Total Social Shares" />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" width="100%" sx={{ border: 0 }}>Clicks</TableCell>
                        <TableCell align="right" sx={{ border: 0 }}>{totals.clicks}</TableCell>
                        <TableCell align="right" sx={{ border: 0 }}>{renderPercentage(totals.clicks, totals.opens)}</TableCell>
                        <TableCell sx={{ border: 0 }}>
                            <IconTooltip title="Social Clicks % - Number of Social Clicks / Total Social Opens" />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const SocialCard = ({ source, actions, isReport, campaignUniqueOpens }) => {
    const getTotal = source => {
        const actionType = actions.find(e => e.action.includes(source));

        return actionType ? actionType.total : 0;
    };

    const getIcon = source => {
        if (source === 'Facebook') {
            return <FacebookIcon fontSize="large" />;
        }

        if (source === 'LinkedIn') {
            return <LinkedInIcon fontSize="large" />;
        }

        if (source === 'Twitter') {
            return <TwitterIcon fontSize="large" />;
        }

        if (source === 'Share') {
            return <ShareIcon fontSize="large" />;
        }

        if (source === 'Teams') {
            return <GroupsIcon fontSize="large" />;
        }

        return null;
    };

    const totals = {
        shares: getTotal('Post'),
        opens: getTotal('Open'),
        clicks: getTotal('Click')
    };

    const totalActivity = Object.values(totals).reduce((a, b) => a + b, 0);

    return (
        <Grid size={{ xs: (isReport ? 6 : 12), md: 6, lg: 4 }}>
            <Card>
                <CardHeader
                    title={source === 'Share' ? 'Share Link' : source}
                    sx={{
                        backgroundColor: getColor(source),
                        color: '#FFF',
                        '@media print': {
                            backgroundColor: getColor(source),
                            color: '#FFF'
                        }
                    }}
                    action={getIcon(source)}
                />
                <CardContent>
                    <SocialTable actions={actions} source={source} totals={totals} campaignUniqueOpens={campaignUniqueOpens} />
                </CardContent>
                <CardActions sx={{ backgroundColor: getColor(source), color: '#FFF' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Total Activity: {totalActivity}</Box>
                </CardActions>
            </Card>
        </Grid>
    );
};

const Social = ({ campaignId, campaignUniqueOpens, isReport = false, parentIsLoading = null, setParentIsLoading = null }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [socialSummary, setSocialSummary] = useState(null);
    const { handleGet } = useApi();

    const handleGetSocialSummary = async () => {
        const url = `reports/${campaignId}/social`;
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        setSocialSummary(await response.json());
    };

    useEffect(() => {
        handleGetSocialSummary();
    }, []);

    useEffect(() => {
        if (socialSummary) {
            if (setParentIsLoading) {
                setParentIsLoading(false);
                return;
            }

            setIsLoading(false);
        }
    }, [socialSummary]);

    if (parentIsLoading === null && isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    if (parentIsLoading) {
        return <Skeleton variant="rectangular" height={100} />;
    }

    return (
        <Box sx={{ margin: 3 }}>
            <Grid container spacing={3}>
                <SocialCard
                    source="Facebook"
                    actions={socialSummary.filter(e => e.source === 'Facebook')}
                    isReport={isReport}
                    campaignUniqueOpens={campaignUniqueOpens}
                />
                <SocialCard
                    source="Twitter"
                    actions={socialSummary.filter(e => e.source === 'Twitter')}
                    isReport={isReport}
                    campaignUniqueOpens={campaignUniqueOpens}
                />
                <SocialCard
                    source="LinkedIn"
                    actions={socialSummary.filter(e => e.source === 'LinkedIn')}
                    isReport={isReport}
                    campaignUniqueOpens={campaignUniqueOpens}
                />
                <SocialCard
                    source="Teams"
                    actions={socialSummary.filter(e => e.source === 'Teams')}
                    isReport={isReport}
                    campaignUniqueOpens={campaignUniqueOpens}
                />
                <SocialCard
                    source="Share"
                    actions={socialSummary.filter(e => e.source === 'Share')}
                    isReport={isReport}
                    campaignUniqueOpens={campaignUniqueOpens}
                />
            </Grid>
        </Box>
    );
};

export default Social;